import { Button } from '@/components';
import { Modal } from '@web3/base-components';
import { useProjectData } from '@/containers/aidol/hooks';
import { LotteryInfo, ModalType } from '@/types/project';
import { appPath, hybirdOpen, justFormatNumber } from '@web3/base-utils';
import BigNumber from 'bignumber.js';
import { useParams, useFm, useHybirdWallet } from '@web3/base-hooks';
import { useScreenType } from '@/hooks/media';
import { scTrack, WEB3_EVENT } from '@web3/base-tracking';
import cls from 'classnames';
import { useUserStatus } from '@/hooks/useUserStatus';
import { UserStatus } from '@/types/user';
import { Page } from '@/config/track-config';
import { formatAmount } from '@/utils';

import styles from './index.module.scss';
import CheckIcon from '@/assets/svg/project/check.svg';
import { BBSOL_ICON, USDT_ICON } from '@/constants';

interface Props {
  show: boolean;
  modalType: ModalType;
  lotteryInfo: LotteryInfo;
  points: string;
  onClose: () => void;
}

export const ResultModal = (props: Props) => {
  const { show, modalType, lotteryInfo, points, onClose } = props;
  const { projectData } = useProjectData();
  const { locale } = useParams();
  const fm = useFm();
  const { isMobile } = useScreenType();
  const { mnemonicChainList } = useHybirdWallet();
  const { userStatus } = useUserStatus();

  const isWin = new BigNumber(lotteryInfo?.rewardAmount).isGreaterThan(0);
  const canEarnMore = new BigNumber(
    projectData.userProjectPointChange
  ).isLessThan(
    userStatus === UserStatus.SPOT_FINISH ||
      userStatus === UserStatus.WALLET_FINISH
      ? `100,400`
      : `50,200`
  );

  return (
    <Modal
      open={show}
      onCancel={onClose}
      footer={null}
      modal={false}
      size="medium"
    >
      <div className={styles.container}>
        <div className={styles.avatarWrap}>
          <img className={styles.avatar} src={projectData.icon} />
          <CheckIcon className={styles.checkIcon} />
        </div>

        <div className={styles.title}>{fm('voteSuccessfully')}</div>
        <div className={styles.desc}>
          <div>
            {fm('contributeToProjNameForAmt', {
              projectName: projectData.name,
              amount: formatAmount(points),
            })}
          </div>
          {canEarnMore && <div>{fm('contributeByTrading')}</div>}
        </div>
        {modalType === ModalType.REWARD_RESULT && (
          <div
            className={cls(styles.rewardArea, {
              [styles.rewardAreaNotWin]: !isWin,
            })}
          >
            {isWin && (
              <div className={styles.rewardImgWrap}>
                <img
                  className={styles.rewardImg}
                  src={
                    lotteryInfo.rewardToken.toUpperCase() === 'USDT'
                      ? USDT_ICON
                      : BBSOL_ICON
                  }
                />
              </div>
            )}
            <div className={styles.rewardText}>
              {isWin
                ? fm('youWinAmtTokNameLotBns', {
                    amount: (
                      <span>{justFormatNumber(lotteryInfo.rewardAmount)}</span>
                    ),
                    tokenName: <span>{lotteryInfo.rewardToken}</span>,
                  })
                : fm('didntWinLotBnsGoodLuck')}
            </div>
          </div>
        )}
        <div className={styles.buttonArea}>
          {canEarnMore && (
            <Button
              className={styles.button}
              variant="primary"
              size={isMobile ? 'small' : 'middle'}
              onClick={() => {
                onClose();

                window.scrollTo({
                  top: isMobile ? 400 : 500,
                  behavior: 'smooth',
                });
              }}
            >
              {fm('earnMorePoints')}
            </Button>
          )}
          <Button
            className={styles.button}
            variant="secondaryBrand"
            size={isMobile ? 'small' : 'middle'}
            onClick={() => {
              scTrack(WEB3_EVENT.Web3UserClick, {
                page_name: Page.DeailPage,
                spmName: projectData.name,
                button_name: 'VoteModalCheckPoints',
              });

              window.open(`/${locale}${appPath}/profile`, '_blank');
            }}
          >
            {fm('myContribute')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
